<template>
  <Card
    :id="id"
    class="stop-popup no-pad"
    :class="{
      pickup: isPickup,
      dropoff: !isPickup,
      'has-geocode-error': isMissingCoords && stop.location?.locationName,
      'missing-location': isMissingCoords && !stop.location?.locationName,
    }"
  >
    <template #header>
      <Avatar
        class="stop-avatar"
        :label="stopLabel ?? stop.sequenceNumber?.toString()"
        aria-hidden="true"
        shape="circle"
      />

      <div class="location-name">{{ stop.location?.locationName ?? '-' }}</div>

      <div class="location-city-state">{{ stop.location?.cityState }}</div>

      <div v-if="isMissingCoords">
        {{
          t(
            `maps.${stop.location?.locationName ? 'geocodeErrorParam' : 'missingLocation'}`,
            { param: t('shipments.stops') },
          )
        }}
      </div>
    </template>

    <template #content>
      <KeyValuePair
        :label="t('lineItems.handling')"
        :value="renderedHandlingUnits"
      />

      <KeyValuePair
        :label="
          t('common.totalParam', {
            param: t('lineItems.weight'),
          })
        "
        :value="
          translateNumberedEntity(
            totalWeight,
            'uoms.weight.abbreviations.POUND',
          )
        "
      />
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { translateNumberedEntity } from '@rygen/rygen-ui/i18n';

import { useLineItemsSummary } from '@/composables';
import {
  LineItem,
  ShipmentStop,
  ShipmentStopType,
  ShippingPoint,
} from '@/model';

import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Avatar from 'primevue/avatar';
import Card from 'primevue/card';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  stop: {
    type: Object as PropType<ShippingPoint>,
    default: () => new ShipmentStop(),
  },
  lineItems: {
    type: Array as PropType<LineItem[]>,
    default: () => [],
  },
  stopLabel: {
    type: String,
    default: () => undefined,
  },
  isMissingCoords: {
    type: Boolean,
    default: () => false,
  },
});
const { id, stop, lineItems, stopLabel, isMissingCoords } = toRefs(props);

const { n, t } = useI18n();
const { totalHandlingUnits, handlingUnits, totalWeight } =
  useLineItemsSummary(lineItems);

const isPickup = computed(
  () => stop.value.stopType === ShipmentStopType.PICKUP,
);

const renderedHandlingUnits = computed(() => {
  const numHandlingUnits = n(totalHandlingUnits.value, 'basic');
  return `${numHandlingUnits} ${handlingUnits.value}`;
});
</script>

<style>
.stop-popup {
  --p-avatar-background: var(--stop-type-accent-color);
  --p-avatar-width: 32px;
  --p-avatar-height: 32px;

  border-left: 4px solid var(--stop-type-accent-color);
  padding: var(--sp-3);
  /* leaflet will clamp this */
  width: max-content;

  &.has-geocode-error {
    --stop-type-accent-color: var(--danger-color);
  }

  .p-card-header {
    display: grid;
    grid-template-areas:
      'avatar location-name'
      'avatar location-city-state';
    column-gap: var(--sp-2);
    row-gap: var(--sp-1);
    padding: 0 0 var(--sp-3) 0;
    border-bottom: 1px solid var(--divider-color);
    width: max-content;

    .stop-avatar {
      grid-area: avatar;
    }

    .location-name {
      grid-area: location-name;
      font-weight: 600;
    }

    .location-city-state {
      grid-area: location-city-state;
      color: var(--p-gray-400);
    }
  }

  .p-card-body {
    .p-card-content {
      display: flex;
      align-items: center;
      padding: var(--sp-3) 0 0 0;

      .key-value-pair {
        width: 50%;
        text-align: center;

        &:first-of-type {
          border-right: 1px solid var(--divider-color);
          margin-right: var(--sp-3);
          padding-right: var(--sp-3);
        }

        .key-value-pair-label {
          /* Same as visually-hidden to keep the element in the DOM but hidden */
          position: absolute;
          width: 1px;
          height: 1px;
          margin: -1px;
          border: 0;
          padding: 0;
          white-space: nowrap;
          clip-path: inset(100%);
          clip: rect(0 0 0 0);
          overflow: hidden;
          /* end visually-hidden rules */
        }

        .key-value-pair-value {
          font-weight: 400;
          color: var(--p-gray-400);
        }
      }
    }
  }
}
</style>
